import {
  MatchType,
  WatchlistSeverityLevel,
  VisitorWatchlistCreateInput,
  VisitorWatchlistUpdateInput,
  VisitorWatchlist,
} from 'graphql-query-contracts';
import { TFunction } from 'i18next';

export const WATCHLIST_LEVELS = [
  {
    label: 'web.admin.channel.visitor.watchlist.highRisk',
    value: WatchlistSeverityLevel.WatchlistSeverityLevelHigh,
  },
];

export interface VisitorWatchlistParams {
  firstname: string;
  lastname: string;
  firstnameAliases: string[];
  lastnameAliases: string[];
  primaryEmail: string;
  additionalEmails: string[];
  primaryPhone: string;
  additionalPhones: string[];
  reportedBy?: string;
  watchlistLevel: WatchlistSeverityLevel;
  watchlistLevelConfigId?: string;
  notes: string;
  attachments: string[];
}

interface VisitorMatchingFieldInput {
  primaryValue: string;
  alternateValues?: string[] | null;
  matchType: MatchType;
}

interface VisitorMatchingInfoInput {
  firstName?: VisitorMatchingFieldInput | null;
  lastName?: VisitorMatchingFieldInput | null;
  email?: VisitorMatchingFieldInput | null;
  phone?: VisitorMatchingFieldInput | null;
}

export const mapVisitorMatchingField = (
  field?: VisitorMatchingFieldInput | null
) => {
  return {
    primaryValue: field?.primaryValue || '',
    alternateValues: field?.alternateValues || [],
    matchType: field?.matchType || MatchType.MatchTypeExact,
  };
};

export interface NameInputFieldsProps {
  visitorWatchlistParams: VisitorWatchlistParams;
  setVisitorWatchlistParams: (params: VisitorWatchlistParams) => void;
  showFirstNameAliases: boolean;
  setShowFirstNameAliases: (show: boolean) => void;
  showLastNameAliases: boolean;
  setShowLastNameAliases: (show: boolean) => void;
  t: TFunction<'translation', undefined>;
}

export interface ContactInputFieldsProps {
  visitorWatchlistParams: VisitorWatchlistParams;
  setVisitorWatchlistParams: (params: VisitorWatchlistParams) => void;
  showAdditionalEmails: boolean;
  setShowAdditionalEmails: (show: boolean) => void;
  emailError: string | null;
  handleEmailChange: (value: string) => void;
  t: TFunction<'translation', undefined>;
}

export const mapVisitorMatchingInfo = (
  visitorMatchingInfo?: VisitorMatchingInfoInput | null
) => {
  return {
    firstName: mapVisitorMatchingField(visitorMatchingInfo?.firstName),
    lastName: mapVisitorMatchingField(visitorMatchingInfo?.lastName),
    email: mapVisitorMatchingField(visitorMatchingInfo?.email),
    phone: mapVisitorMatchingField(visitorMatchingInfo?.phone),
  };
};

export const createVisitorWatchlistInput = (
  params: VisitorWatchlistParams,
  organizationId: string,
  userId: string,
  isWatchlistV2Enabled: boolean = false
): VisitorWatchlistCreateInput => {
  const rawVisitorMatchingInfo = {
    firstName: {
      primaryValue: params.firstname.trim(),
      alternateValues: params.firstnameAliases,
      matchType: MatchType.MatchTypeExact,
    },
    lastName: {
      primaryValue: params.lastname.trim(),
      alternateValues: params.lastnameAliases,
      matchType: MatchType.MatchTypeExact,
    },
    email: params.primaryEmail.trim()
      ? {
          primaryValue: params.primaryEmail.trim(),
          alternateValues: params.additionalEmails,
          matchType: MatchType.MatchTypeExact,
        }
      : undefined,
    phone: params.primaryPhone.trim()
      ? {
          primaryValue: params.primaryPhone.trim(),
          alternateValues: params.additionalPhones,
          matchType: MatchType.MatchTypeExact,
        }
      : undefined,
  };

  return {
    organizationId,
    visitorMatchingInfo: mapVisitorMatchingInfo(rawVisitorMatchingInfo),
    severityLevel: isWatchlistV2Enabled
      ? undefined
      : WatchlistSeverityLevel.WatchlistSeverityLevelHigh,
    notes: params.notes || undefined,
    attachmentIds: params.attachments,
    reportedBy: params.reportedBy || undefined,
    createdBy: userId,
    updatedBy: userId,
    watchlistLevelConfigId: isWatchlistV2Enabled
      ? params.watchlistLevelConfigId
      : undefined,
  };
};

export const getInitialVisitorWatchlistParams = (): VisitorWatchlistParams => ({
  firstname: '',
  lastname: '',
  firstnameAliases: [],
  lastnameAliases: [],
  primaryEmail: '',
  additionalEmails: [],
  primaryPhone: '',
  additionalPhones: [],
  reportedBy: '',
  watchlistLevel: WatchlistSeverityLevel.WatchlistSeverityLevelHigh,
  watchlistLevelConfigId: undefined,
  notes: '',
  attachments: [],
});

export const mapVisitorWatchlistToParams = (
  watchlist: VisitorWatchlist,
  isWatchlistV2Enabled: boolean = false
): VisitorWatchlistParams => {
  const matchingInfo = watchlist.visitorMatchingInfo;
  
  return {
    firstname: matchingInfo?.firstName?.primaryValue || '',
    lastname: matchingInfo?.lastName?.primaryValue || '',
    firstnameAliases: matchingInfo?.firstName?.alternateValues || [],
    lastnameAliases: matchingInfo?.lastName?.alternateValues || [],
    primaryEmail: matchingInfo?.email?.primaryValue || '',
    additionalEmails: matchingInfo?.email?.alternateValues || [],
    primaryPhone: matchingInfo?.phone?.primaryValue || '',
    additionalPhones: matchingInfo?.phone?.alternateValues || [],
    reportedBy: watchlist.reportedBy || '',
    watchlistLevel: isWatchlistV2Enabled
      ? (watchlist.watchlistLevelConfig?.level || WatchlistSeverityLevel.WatchlistSeverityLevelHigh)
      : WatchlistSeverityLevel.WatchlistSeverityLevelHigh,
    watchlistLevelConfigId: isWatchlistV2Enabled
      ? watchlist.watchlistLevelConfig?.id
      : undefined,
    notes: watchlist.notes || '',
    attachments: watchlist.attachmentIds || [],
  };
};

export const updateVisitorWatchlistInput = (
  params: VisitorWatchlistParams,
  watchlistId: string,
  organizationId: string,
  userId: string,
  isWatchlistV2Enabled: boolean = false
): VisitorWatchlistUpdateInput => {
  const rawVisitorMatchingInfo = {
    firstName: {
      primaryValue: params.firstname.trim(),
      alternateValues: params.firstnameAliases,
      matchType: MatchType.MatchTypeExact,
    },
    lastName: {
      primaryValue: params.lastname.trim(),
      alternateValues: params.lastnameAliases,
      matchType: MatchType.MatchTypeExact,
    },
    email: params.primaryEmail.trim()
      ? {
          primaryValue: params.primaryEmail.trim(),
          alternateValues: params.additionalEmails,
          matchType: MatchType.MatchTypeExact,
        }
      : undefined,
    phone: params.primaryPhone.trim()
      ? {
          primaryValue: params.primaryPhone.trim(),
          alternateValues: params.additionalPhones,
          matchType: MatchType.MatchTypeExact,
        }
      : undefined,
  };

  return {
    visitorWatchlistId: watchlistId,
    organizationId,
    visitorMatchingInfo: mapVisitorMatchingInfo(rawVisitorMatchingInfo),
    severityLevel: isWatchlistV2Enabled
      ? undefined
      : WatchlistSeverityLevel.WatchlistSeverityLevelHigh,
    notes: params.notes || undefined,
    attachmentIds: params.attachments,
    reportedBy: params.reportedBy || undefined,
    updatedBy: userId,
    watchlistLevelConfigId: isWatchlistV2Enabled
      ? params.watchlistLevelConfigId
      : undefined,
  };
};
